import { z } from 'zod'

const text = z.string().nullish()

/**
 * Where a generated version stands with the client. Only the version that is PENDING_REVIEW is the one the client sees, and only one
 * version can be at a time; the newest version is never taken to be the client's. When the client asks for changes the version goes back to
 * DRAFT. A version the server gave no (or an unknown) status is a DRAFT.
 */
export const VERSION_STATUSES = ['DRAFT', 'PENDING_REVIEW', 'APPROVED'] as const
export type VersionStatus = (typeof VERSION_STATUSES)[number]

export function versionStatusOf(raw: string | null | undefined): VersionStatus {
  const word = (raw ?? '').trim().replace(/([a-z])([A-Z])/g, '$1_$2').replace(/[\s-]+/g, '_').toUpperCase()
  // “In review” is the same thing as pending review.
  return VERSION_STATUSES.find((status) => status === (word === 'IN_REVIEW' ? 'PENDING_REVIEW' : word)) ?? 'DRAFT'
}

/**
 * One generated version of a project's design. `requirementDocsAnalysis` is what the server read from the requirements (the art
 * content: a title, daily specials, sections of menu items) and `instructionAnalysis` is what it read from the instructions (the creative
 * brief: theme, colour palette, typography, layout and more). Both are JSON objects (older ones may be text), so they are kept as they
 * come and shown by what is in them. `generatedImageUrls` are the design's pictures, one for each page (a two-sided menu has two), as
 * paths in the server's storage; the list is empty until the design has been made. An older single `generatedImageUrl` is still read, and
 * counts as the one picture. `regenerationInstructions` is what the person asked for when this version was regenerated. The server sends the list of versions as a plain array, not a page.
 */
export const generationVersionSchema = z
  .object({
    id: z.string(),
    projectId: text,
    accountId: text,
    versionNumber: z.number().default(0),
    /** Read through `versionStatusOf`. */
    status: text,
    instructionAnalysis: z.unknown().optional(),
    requirementDocsAnalysis: z.unknown().optional(),
    generatedImageUrls: z.array(z.string()).nullish(),
    /** The earlier form: one picture. Folded into `generatedImageUrls`. */
    generatedImageUrl: text,
    /** What the person asked for when they regenerated, in words: the instructions this version was made from. Empty for the first. */
    regenerationInstructions: text,
    createdBy: text,
    createdAt: text,
    updatedAt: text,
  })
  .transform(({ generatedImageUrl, generatedImageUrls, regenerationInstructions, status, ...version }) => ({
    ...version,
    status: versionStatusOf(status),
    regenerationInstructions: regenerationInstructions?.trim() ? regenerationInstructions.trim() : null,
    generatedImageUrls: [...new Set([...(generatedImageUrls ?? []), ...(generatedImageUrl ? [generatedImageUrl] : [])].filter((path) => path.trim() !== ''))],
  }))
export type GenerationVersionDto = z.infer<typeof generationVersionSchema>

/**
 * What POST /api/projects/{id}/existing-template answers: the project as it now stands (the template's `assetId` linked, its first
 * picture named) and the project's first version, with the template as its picture. A bare version (the earlier answer) is still read.
 */
export const existingTemplateResponseSchema = z.union([
  z.object({
    project: z.object({ id: z.string(), status: text, assetId: text, generatedVersionNumber: z.number().nullish(), generatedImageUrls: z.array(z.string()).nullish() }).nullish(),
    generationVersion: generationVersionSchema,
  }),
  generationVersionSchema.transform((generationVersion) => ({ project: null, generationVersion })),
])
export type ExistingTemplateResponseDto = z.infer<typeof existingTemplateResponseSchema>

export const generationVersionListSchema = z.array(generationVersionSchema).nullish().transform((items) => items ?? [])

/**
 * POST /api/projects/{id}/generate-image. `taskId` is the background task that makes the picture; follow it with the sse-tasks API
 * (`eventsUrl` is its event stream). `alreadyRunning` is true when a design is already being made, and the task named is that one.
 */
export const generateImageResponseSchema = z.object({
  taskId: z.string(),
  projectId: text,
  status: text,
  progress: z.number().default(0),
  alreadyRunning: z.boolean().default(false),
  eventsUrl: text,
})
export type GenerateImageResponseDto = z.infer<typeof generateImageResponseSchema>

/**
 * POST /api/projects/{id}/regenerate. Like `generate-image`, it starts a background task (`taskId`, followed through its event stream), and
 * it also names the new version it is making (`versionId`, `versionNumber`) and the model it uses.
 */
export const regenerateResponseSchema = z.object({
  taskId: z.string(),
  projectId: text,
  versionId: text,
  versionNumber: z.number().nullish(),
  status: text,
  progress: z.number().default(0),
  model: text,
  eventsUrl: text,
})
export type RegenerateResponseDto = z.infer<typeof regenerateResponseSchema>

/**
 * PUT /api/projects/{id}/generation-versions/{versionId}. Send only what changes: a field left out is not touched; a field sent replaces
 * the stored analysis completely and must be a non-empty JSON object. Image generation reads these, so an edit takes effect the next time
 * artwork is made.
 */
export interface UpdateAnalysisBody {
  instructionAnalysis?: Record<string, unknown>
  requirementDocsAnalysis?: Record<string, unknown>
}

/** The same PUT, sent with only a status: sending a version for review, or recording the client's decision on it. */
export interface UpdateVersionStatusBody {
  status: VersionStatus
}

/**
 * The version the client sees: the one pending review (only one can be), and once the project is approved the approved one. A version the
 * client sent back is a draft again, so until another is sent they see nothing. Never the newest version just because it is the newest.
 */
export function clientVersionOf<T extends { status: VersionStatus; generatedImageUrls: string[] }>(versions: readonly T[], projectStatus: string | null | undefined): T | undefined {
  const withPictures = versions.filter((version) => version.generatedImageUrls.length > 0)
  const of = (status: VersionStatus) => withPictures.find((version) => version.status === status)
  if (projectStatus === 'APPROVED' || projectStatus === 'COMPLETED') return of('APPROVED') ?? of('PENDING_REVIEW')
  return of('PENDING_REVIEW')
}

/**
 * The master template of an approved project: the generated version whose status is APPROVED, not whichever was made last. Where the server
 * holds no approved version (an older project), the version the decision here names stands in, and, failing that, the newest with pictures.
 */
export function masterVersionOf<T extends { id: string; status: VersionStatus; generatedImageUrls: string[] }>(versions: readonly T[], decidedId?: string | null): T | undefined {
  const withPictures = versions.filter((version) => version.generatedImageUrls.length > 0)
  return withPictures.find((version) => version.status === 'APPROVED') ?? withPictures.find((version) => version.id === decidedId) ?? withPictures[0]
}

/** What to ask for: the instructions in words, and optionally which model to use (the server picks one when it is left out). */
export interface RegenerateBody {
  instructions: string
  model?: string
}
